import { BasicAudience } from "modules/audiences";
import { MultiSelectTopicGroup } from "modules/settings";
import { MultiSelectTopic } from "modules/settings/models";

type TopicStatValue = number | "-";
export type TopicType = "Default" | "Mandatory" | "Standard";
export type TopicState = "Enabled" | "Disabled";
export type TopicAccess = "Restricted" | "Non-restricted";

export const allTopicTypes: TopicType[] = ["Default", "Mandatory", "Standard"];
export const allTopicStates: TopicState[] = ["Enabled", "Disabled"];
export const allTopicAccess: TopicAccess[] = ["Restricted", "Non-restricted"];

export interface TopicsState {
    topicGroups: TopicGroup[];
    isFetching: boolean;
    isUpdating: boolean;
    successMessage: string;
    errorMessage: string;
}

export enum TopicErrorMessage {
    TopicGroup = "Topic group already exists.",
    Topic = "Topic name already exists in group.",
    Move = "Topic name already exists in the selected group."
}

export interface GetTopicFilters {
    access?: TopicAccess[];
    state?: TopicState[];
    type?: TopicType[];
    textToSearch?: string;
    lcidToSearch?: string;
    ascending: boolean;
    isFilterEnabled: boolean;
}

export const defaultTopicFilter: GetTopicFilters = {
    access: [],
    state: [],
    type: [],
    textToSearch: "",
    lcidToSearch: "",
    ascending: true,
    isFilterEnabled: false
};

export interface TopicDetails {
    id: string;
    name: string;
    disabled: boolean | null;
    groupId: string | null;
    default: boolean;
    mandatory: boolean;
    restricted: boolean;
    defaultFor: BasicAudience[];
    mandatoryFor: BasicAudience[];
    restrictedTo: BasicAudience[];
}

export const emptyTopicDetails: TopicDetails = {
    id: "",
    name: "",
    groupId: "",
    disabled: false,
    default: false,
    mandatory: false,
    restricted: false,
    defaultFor: [],
    mandatoryFor: [],
    restrictedTo: []
};

export const awaitingEditInput: TopicDetails = {
    id: "",
    name: "",
    groupId: null,
    disabled: null,
    default: false,
    mandatory: false,
    restricted: false,
    defaultFor: [],
    mandatoryFor: [],
    restrictedTo: []
};

export interface TopicToggleStatus {
    isRestricted: boolean;
    isMandatory: boolean;
    isDefault: boolean;
}

export interface AudienceByPermission {
    restricted: BasicAudience[];
    mandatory: BasicAudience[];
    default: BasicAudience[];
}

export interface TopicReportRequest {
    topicName: string,
    dateRequested: string,
    clientTimeZone: string,
    clientTimeOffset: string,
    contentId: string,
    type: string,
}

export interface TopicData {
    id: string,
    name: string,
    restricted: boolean,
    disabled: boolean,
    type: TopicType[],
    subs: TopicStatValue,
    posts: TopicStatValue,
    events: TopicStatValue,
    views: TopicStatValue,
}

export const defaultTopicData = (topicSettings: Topic): TopicData => {
    const isTypes = [topicSettings.default, topicSettings.mandatory, !topicSettings.default && !topicSettings.mandatory];
    return (
        {
            id: topicSettings.id,
            name: topicSettings.name,
            restricted: topicSettings.restricted,
            disabled: topicSettings.disabled,
            type: allTopicTypes.filter((_, index) => isTypes[index]),
            subs: "-",
            posts: "-",
            events: "-",
            views: "-"
        }
    );
};

export interface TopicGroup extends MultiSelectTopicGroup {
    id: string;
    name: string;
    tags: Topic[];
}

export interface BasicTopicGroup {
    id: string;
    label: string;
}

export interface Topic extends MultiSelectTopic {
    id: string;
    name: string;
    restricted: boolean;
    mandatory: boolean;
    default: boolean;
    disabled: boolean;
}

export interface BasicTopic {
    id: string;
    name: string;
}